import { useIntl } from "react-intl";
import { useCarHireExitLink } from "src/exits/hooks/useCarHireExitLink";
import { getCarHirePickupPlace } from "src/utils/getCarHirePickupPlace";
import type { SearchResponse } from "src/api/SearchResponse";
import type { AnalyticsEventCategory } from "src/analytics/analyticsEventTypes";
import type { ExitLinkProps } from "src/exits/hooks/useExitLink";
import { PromoContainer } from "../SearchPromoContainer";
import { PromoGallery } from "../SearchPromoGallery";
import { PromoViewMoreButton } from "../SearchPromoViewMore";
import messages from "./CarPromo.messages";
import { CarOption } from "./CarOption";
import smallCarWebp from "./smallCar.png?w=150;300;450&format=webp&as=srcset&imagetools";
import smallCar from "./smallCar.png?w=150;300;450&as=srcset&imagetools";
import mediumCarWebp from "./mediumCar.png?w=150;300;450&format=webp&as=srcset&imagetools";
import mediumCar from "./mediumCar.png?w=150;300;450&as=srcset&imagetools";
import largeCarWebp from "./largeCar.png?w=150;300;450&format=webp&as=srcset&imagetools";
import largeCar from "./largeCar.png?w=150;300;450&as=srcset&imagetools";

export function CarPromo({
  searchResponse,
  eventCategory,
}: {
  searchResponse: SearchResponse;
  eventCategory: AnalyticsEventCategory;
}) {
  const intl = useIntl();
  const { carPickupPlace } = getCarHirePickupPlace(searchResponse);

  const exitPoint: ExitLinkProps["exitPoint"] = {
    view: "searchResults",
    trigger: "carCta",
    transitMode: "car",
  };

  const event = {
    category: eventCategory,
    action: "Click:CarHirePromo",
  };

  const [_smallUrl, handleSmallClick] = useCarHireExitLink({
    event,
    exitPoint,
    carSize: "small",
  });

  const [_mediumUrl, handleMediumClick] = useCarHireExitLink({
    event,
    exitPoint,
    carSize: "medium",
  });

  const [_largeUrl, handleLargeClick] = useCarHireExitLink({
    event,
    exitPoint,
    carSize: "large",
  });

  const [_url, handleClick] = useCarHireExitLink({
    event,
    exitPoint,
  });

  return (
    <PromoContainer>
      <h2 className="text-heading-md">
        {intl.formatMessage(messages.heading, {
          location: carPickupPlace?.shortName,
        })}
      </h2>
      <PromoGallery>
        <CarOption
          onPress={handleSmallClick}
          srcSet={{
            webp: smallCarWebp,
            fallback: smallCar,
          }}
          label={intl.formatMessage(messages.smallCar)}
        />
        <CarOption
          onPress={handleMediumClick}
          srcSet={{
            webp: mediumCarWebp,
            fallback: mediumCar,
          }}
          label={intl.formatMessage(messages.mediumCar)}
        />
        <CarOption
          onPress={handleLargeClick}
          srcSet={{
            webp: largeCarWebp,
            fallback: largeCar,
          }}
          label={intl.formatMessage(messages.largeCar)}
        />
      </PromoGallery>
      <PromoViewMoreButton onPress={handleClick} />
    </PromoContainer>
  );
}
